import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiPlus, FiSave } from 'react-icons/fi';
import { API_URLS, getStadiumImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { useAuth } from '../utilities/auth';
import { filterChampionshipOptions, useAdminAccess } from '../utilities/adminAccess';
import {
  authorizedPost,
  fileToBase64,
  getApiErrorMessage,
  getChampId,
  loadChampionshipOptions,
} from '../utilities/stadiumsApi';
import AdminPhotoUpload from '../components/AdminPhotoUpload';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import CustomModal from '../components/CustomModal';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';

const emptyForm = {
  enabled: true,
  name: '',
  address: '',
  description: '',
  lat: '',
  long: '',
  pitchSize: '',
  pitchCover: '',
  capacity: '',
  championat_id: '',
};

export default function ManageStadium() {
  const { stadiumId } = useParams();
  const isNew = !stadiumId;
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { userData } = useAuth();
  const { canWrite } = useAdminAccess('stadiums');

  const [form, setForm] = useState(emptyForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [errors, setErrors] = useState({});
  const [championships, setChampionships] = useState([]);
  const [pitches, setPitches] = useState([]);
  const [originalPitches, setOriginalPitches] = useState([]);
  const [reorderMode, setReorderMode] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    initPage();
  }, [stadiumId]);

  const initPage = async () => {
    setMessage({ text: '', type: '' });
    setErrors({});
    setPhotoFile(null);
    setPhotoError('');
    setReorderMode(false);

    try {
      const { champId, options } = await loadChampionshipOptions(dispatch);
      const nextOptions = isNew
        ? filterChampionshipOptions(options, userData, 'stadiums', { write: true })
        : options;
      setChampionships(nextOptions);

      if (isNew) {
        const defaultChamp = nextOptions.some((item) => Number(item.value) === Number(champId))
          ? champId
          : (nextOptions[0] && nextOptions[0].value) || '';
        setForm({
          ...emptyForm,
          championat_id: defaultChamp,
        });
        setPhotoPreview('');
        setPitches([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const response = await authorizedPost(dispatch, API_URLS.getAdminStadium, {
        id: Number(stadiumId),
        champ_id: champId || undefined,
      });
      const stadium = response.data || {};
      setForm({
        enabled: Number(stadium.enabled) === 1,
        name: stadium.name || '',
        address: stadium.address || '',
        description: stadium.description || '',
        lat: stadium.lat || '',
        long: stadium.long || '',
        pitchSize: stadium.pitchSize || '',
        pitchCover: stadium.pitchCover || '',
        capacity: stadium.capacity == null ? '' : String(stadium.capacity),
        championat_id: stadium.championat_id || champId || '',
      });
      const imagePath = stadium.image ? getStadiumImageUrl(stadium.image) : '';
      setPhotoPreview(imagePath ? `${imagePath}${imagePath.includes('?') ? '&' : '?'}v=${Date.now()}` : '');
      const nextPitches = Array.isArray(stadium.pitches) ? stadium.pitches : [];
      setPitches(nextPitches);
      setOriginalPitches(nextPitches);
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = t('stdNameRequired');
    if (isNew && !form.championat_id) nextErrors.championat_id = t('stChampionshipRequired');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = async () => {
    const payload = {
      name: form.name.trim(),
      address: form.address.trim(),
      enabled: form.enabled ? 1 : 0,
      description: form.description.trim(),
      lat: form.lat.trim() || null,
      long: form.long.trim() || null,
      pitchSize: form.pitchSize.trim() || null,
      pitchCover: form.pitchCover.trim() || null,
      capacity: form.capacity === '' ? null : Number(form.capacity),
      championat_id: Number(form.championat_id) || getChampId(),
    };
    if (photoFile) {
      payload.image = await fileToBase64(photoFile);
    }
    return payload;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = await buildPayload();
      if (isNew) {
        const response = await authorizedPost(dispatch, API_URLS.addStadium, payload);
        const created = response.data || {};
        setMessage({ text: t('stdCreated'), type: 'success' });
        if (created.id) {
          history.replace(`/stadiums_management/${created.id}`);
        } else {
          history.push('/stadiums_management');
        }
      } else {
        await authorizedPost(dispatch, API_URLS.changeStadium, {
          ...payload,
          id: Number(stadiumId),
        });
        setPhotoFile(null);
        setMessage({ text: t('stSaved'), type: 'success' });
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await authorizedPost(dispatch, API_URLS.deleteStadium, { id: Number(stadiumId) });
      history.push('/stadiums_management');
    } catch (error) {
      setDeleteVisible(false);
      setMessage({ text: getApiErrorMessage(error, t('stdDeleteError')), type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleSavePitchOrder = async () => {
    setSavingOrder(true);
    setMessage({ text: '', type: '' });
    try {
      const ordered = pitches.map((pitch, index) => ({
        ...pitch,
        priority: index + 1,
      }));
      await authorizedPost(dispatch, API_URLS.changePitchesPriority, {
        stadium_id: Number(stadiumId),
        pitches: ordered.map((pitch) => ({
          id: pitch.id,
          priority: pitch.priority,
        })),
      });
      setPitches(ordered);
      setOriginalPitches(ordered);
      setReorderMode(false);
      setMessage({ text: t('stOrderSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stOrderError')), type: 'error' });
    } finally {
      setSavingOrder(false);
    }
  };

  const pitchColumns = [
    {
      key: 'name',
      label: t('stdName'),
      className: 'st-cell-name',
      render: (item) => {
        const statusLabel = Number(item.enabled) === 1 ? t('stEnabledShort') : t('stDisabledShort');
        return (
          <span className="std-stadium-main">
            <span className="std-stadium-name">{item.name || '—'}</span>
            <small className="std-stadium-meta std-pitch-meta">{statusLabel}</small>
          </span>
        );
      },
    },
    {
      key: 'enabled',
      label: t('stStatus'),
      className: 'st-cell-status',
      render: (item) => (
        <span className={`st-badge ${Number(item.enabled) === 1 ? 'enabled' : 'disabled'}`}>
          <span className="tr-btn-full">{Number(item.enabled) === 1 ? t('stEnabled') : t('stDisabled')}</span>
          <span className="tr-btn-short">{Number(item.enabled) === 1 ? t('stEnabledShort') : t('stDisabledShort')}</span>
        </span>
      ),
    },
    {
      key: 'priority',
      label: t('stdPriority'),
      className: 'st-cell-priority',
      render: (item, index) => (
        <span className="st-priority-badge">{reorderMode ? index + 1 : (item.priority ?? index + 1)}</span>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs
                items={[
                  { label: t('navStadiums'), to: '/stadiums_management' },
                  { label: isNew ? t('stNewStadium') : t('stEditStadium') },
                ]}
              />
            </div>
          </div>
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('stLoading')}</p>
          </div>
        </div>
      </div>
    );
  }

  const showChampionshipSelect = isNew && championships.length !== 1;

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page std-edit-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs
              items={[
                { label: t('navStadiums'), to: '/stadiums_management' },
                { label: isNew ? t('stNewStadium') : (form.name.trim() || t('stEditStadium')) },
              ]}
            />
          </div>
        </div>

        <div className="st-scroll">
          <div className="st-content">
            {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

            <div className="st-block">
              <div className="st-form-grid">
                <div className="st-field st-field-wide">
                  <label className="st-check">
                    <input
                      type="checkbox"
                      checked={form.enabled}
                      onChange={(event) => updateField('enabled', event.target.checked)}
                    />
                    {t('stShowOnSite')}
                  </label>
                </div>

                {showChampionshipSelect ? (
                  <div className="st-field">
                    <label>{t('championship')} <span className="st-required">*</span></label>
                    <select
                      className={`st-select ${errors.championat_id ? 'error' : ''}`}
                      value={form.championat_id}
                      onChange={(event) => updateField('championat_id', event.target.value)}
                    >
                      <option value="">{t('championshipPlaceholder')}</option>
                      {championships.map((item) => (
                        <option key={item.value} value={item.value}>{item.name}</option>
                      ))}
                    </select>
                    {errors.championat_id ? <span className="st-field-error">{errors.championat_id}</span> : null}
                  </div>
                ) : null}

                <div className="st-field">
                  <label>{t('stdName')} <span className="st-required">*</span></label>
                  <input
                    className={`st-input ${errors.name ? 'error' : ''}`}
                    value={form.name}
                    placeholder={t('stdNamePh')}
                    onChange={(event) => updateField('name', event.target.value)}
                  />
                  {errors.name ? <span className="st-field-error">{errors.name}</span> : null}
                </div>

                <div className="st-field">
                  <label>{t('stAddress')}</label>
                  <input
                    className="st-input"
                    value={form.address}
                    placeholder={t('stAddressPh')}
                    onChange={(event) => updateField('address', event.target.value)}
                  />
                </div>

                <div className="st-field st-field-wide">
                  <label>{t('stDescription')}</label>
                  <textarea
                    className="st-textarea"
                    value={form.description}
                    placeholder={t('stDescriptionPh')}
                    onChange={(event) => updateField('description', event.target.value)}
                  />
                </div>

                <div className="st-field">
                  <label>{t('stLat')}</label>
                  <input
                    className="st-input"
                    value={form.lat}
                    placeholder={t('stLatPh')}
                    onChange={(event) => updateField('lat', event.target.value)}
                  />
                </div>

                <div className="st-field">
                  <label>{t('stLong')}</label>
                  <input
                    className="st-input"
                    value={form.long}
                    placeholder={t('stLongPh')}
                    onChange={(event) => updateField('long', event.target.value)}
                  />
                </div>

                <div className="st-field">
                  <label>{t('stPitchSize')}</label>
                  <input
                    className="st-input"
                    value={form.pitchSize}
                    placeholder={t('stPitchSizePh')}
                    onChange={(event) => updateField('pitchSize', event.target.value)}
                  />
                </div>

                <div className="st-field">
                  <label>{t('stPitchCover')}</label>
                  <input
                    className="st-input"
                    value={form.pitchCover}
                    placeholder={t('stPitchCoverPh')}
                    onChange={(event) => updateField('pitchCover', event.target.value)}
                  />
                </div>

                <div className="st-field">
                  <label>{t('stCapacity')}</label>
                  <input
                    type="number"
                    min="0"
                    className="st-input"
                    value={form.capacity}
                    placeholder={t('stCapacityPh')}
                    onChange={(event) => updateField('capacity', event.target.value)}
                  />
                </div>

                <div className="st-field st-field-wide">
                  <label>{t('stPhoto')}</label>
                  <AdminPhotoUpload
                    preview={photoPreview}
                    error={photoError}
                    disabled={saving}
                    onFileChange={(file, error, preview) => {
                      if (error) {
                        setPhotoError(error);
                        return;
                      }
                      setPhotoError('');
                      setPhotoFile(file);
                      setPhotoPreview(preview);
                    }}
                  />
                </div>
              </div>
            </div>

            {!isNew ? (
              <div className="st-block">
                <h2 className="st-block-header">{t('stPitches')}</h2>
                <ReorderableTable
                  tableClassName="st-table-pitches"
                  columns={pitchColumns}
                  items={pitches}
                  getRowKey={(item) => item.id}
                  reorderMode={reorderMode}
                  onItemsChange={setPitches}
                  getRowHref={(item) => (item.id ? `/stadiums_management/${stadiumId}/pitch/${item.id}` : null)}
                  empty={<div className="st-empty"><p>{t('stNoPitches')}</p></div>}
                />
                {reorderMode ? <p className="st-hint">{t('stDragHint')}</p> : null}
                {canWrite ? (
                  <div className="st-actions st-block-actions">
                    {reorderMode ? (
                      <>
                        <button
                          type="button"
                          className="st-btn st-btn-secondary"
                          disabled={savingOrder}
                          onClick={() => {
                            setPitches(originalPitches);
                            setReorderMode(false);
                          }}
                        >
                          {t('stCancelReorder')}
                        </button>
                        <button
                          type="button"
                          className="st-btn st-btn-primary"
                          disabled={savingOrder || !pitches.length}
                          onClick={handleSavePitchOrder}
                        >
                          <FiSave /> {t('stSaveOrder')}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="st-btn st-btn-secondary"
                          disabled={pitches.length < 2}
                          onClick={() => {
                            setOriginalPitches(pitches);
                            setReorderMode(true);
                          }}
                        >
                          <span className="tr-btn-full">{t('stReorder')}</span>
                          <span className="tr-btn-short">{t('stReorderShort')}</span>
                        </button>
                        <Link
                          to={`/stadiums_management/${stadiumId}/pitch/new`}
                          className="st-btn st-btn-primary"
                        >
                          <FiPlus />
                          <span className="tr-btn-full">{t('stAddPitch')}</span>
                          <span className="tr-btn-short">{t('stAddPitchShort')}</span>
                        </Link>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {canWrite ? (
        <div className={`st-actions ${!isNew ? 'st-actions-split' : ''}`}>
          {!isNew ? (
            <button
              type="button"
              className="st-btn st-btn-danger"
              disabled={saving || deleting}
              onClick={() => setDeleteVisible(true)}
            >
              {t('delete')}
            </button>
          ) : <span />}
          <button
            type="button"
            className="st-btn st-btn-primary"
            disabled={saving}
            onClick={handleSave}
          >
            <FiSave />
            <span className="tr-btn-full">{saving ? t('stLoading') : t('stdSaveChanges')}</span>
            <span className="tr-btn-short">{saving ? t('stLoading') : t('stdSaveChangesShort')}</span>
          </button>
        </div>
        ) : null}
      </div>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={deleteVisible}
        closeModal={() => setDeleteVisible(false)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('stDeleteStadiumTitle')}</div>
          <p>{t('stDeleteStadiumText')}</p>
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setDeleteVisible(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-danger-modal" disabled={deleting} onClick={handleDelete}>
              {t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
